import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Grid, Input, Select } from "antd";
import { ContentFilters } from "components";
import { StandardItemsWrapper } from "components/content-filters/content-filters.styles";
import dayjs from "dayjs";
import { useSourceBookings } from "features/bookings/use-cases/get-source-booking";
import { SportSelect } from "features/sports/components/sport-select/sport-select";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS } from "utils/date";

import { BookingAdvancedFilterForm } from "./bookings-advanced-filter-form";
import { BookingFilterFormField, BookingFilterFormValues } from "./types";

const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;

type BookingsFilterFormProps = {
  formId: string;
  handleFilter: (values: BookingFilterFormValues) => void;
  initialValues: BookingFilterFormValues;
  closeModal?: () => void;
};

export const BookingsFilterForm = ({
  formId,
  handleFilter,
  initialValues,
  closeModal,
}: BookingsFilterFormProps) => {
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState(false);
  const [validateTrigger, setValidateTrigger] = useState(["onSubmit"]);
  const [form] = Form.useForm<BookingFilterFormValues>();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { data } = useSourceBookings("" as any);

  const onReset = () => {
    form.resetFields();
  };

  const handleOnFinish = (values: BookingFilterFormValues) => {
    handleFilter(values);
    closeModal?.();
  };

  const toggleAdvancedFilters = () => {
    setOpenAdvancedFilters(!openAdvancedFilters);
  };

  return (
    <Form<BookingFilterFormValues>
      id={formId}
      name={formId}
      form={form}
      layout="vertical"
      onFinish={handleOnFinish}
      initialValues={initialValues}
      onFinishFailed={() => {
        setValidateTrigger(["onChange"]);
      }}
      validateTrigger={validateTrigger}
    >
      <ContentFilters.FormItemsWrapper>
        <StandardItemsWrapper>
          <Form.Item<BookingFilterFormValues>
            label={t("bookings.formFields.date.label")}
            name={BookingFilterFormField.Date}
          >
            <RangePicker
              style={{ maxWidth: "230px" }}
              allowEmpty={[true, true]}
              format={DATE_FORMATS.date}
              presets={[
                {
                  label: t("form.formFields.date.presets.today"),
                  value: [dayjs(), dayjs()],
                },
                {
                  label: t("form.formFields.date.presets.fromTomorrow"),
                  value: [dayjs().add(1, "day"), null],
                },
                {
                  label: t("form.formFields.date.presets.untilYesterday"),
                  value: [null, dayjs().add(-1, "day")],
                },
              ]}
            />
          </Form.Item>
          <Form.Item<BookingFilterFormValues>
            label={t("bookings.formFields.customerName.label")}
            name={BookingFilterFormField.FullUserName}
          >
            <Input
              placeholder={t("bookings.formFields.customerName.placeholder")}
            />
          </Form.Item>
          {/* <Form.Item<BookingFilterFormValues>
            label={t("bookings.formFields.phoneNumber.label")}
            name={BookingFilterFormField.PhoneNumber}
          >
            <Input
              type="tel"
              placeholder={t("bookings.formFields.phoneNumber.placeholder")}
            />
          </Form.Item> */}
          <Form.Item<BookingFilterFormValues>
            label={t("bookings.formFields.phoneNumber.label")}
            name={BookingFilterFormField.PhoneNumber}
          >
            <Input
              type="tel"
              placeholder={t("bookings.formFields.phoneNumber.placeholder")}
            />
          </Form.Item>
          <Form.Item<BookingFilterFormValues>
            label="Sport"
            name={BookingFilterFormField.Sport}
          >
            <SportSelect className="filter filter-select-height" />
          </Form.Item>
          <Form.Item<BookingFilterFormValues>
            label={t("bookings.formFields.source.label")}
            name={BookingFilterFormField.Source}
          >
            <Select
              style={{ width: "120px" }}
              placeholder="Please select Source"
              options={data?.map((item: any) => ({
                label: item.name,
                value: item.id,
              }))}
              className="filter-select-height"
            />
          </Form.Item>
        </StandardItemsWrapper>
        <div style={{ marginRight: "10px", minWidth: "auto" }}>
          <BookingAdvancedFilterForm
            openAdvancedFilters={openAdvancedFilters}
          />
        </div>
        <div className="order-id_a" style={{ minWidth: "auto" }}>
          <StandardItemsWrapper>
            <Form.Item<BookingFilterFormValues>
              name={BookingFilterFormField.Auth}
              label="Auth Id"
              className=""
            >
              <Input placeholder={"Enter Auth Id"} />
            </Form.Item>
            <Form.Item<BookingFilterFormValues>
              name={BookingFilterFormField.tranId}
              label="Order Id"
            >
              <Input placeholder={"Enter Order Id"} />
            </Form.Item>
          </StandardItemsWrapper>
        </div>
      </ContentFilters.FormItemsWrapper>

      {screens.lg && (
        <ContentFilters.ButtonsWrapper>
          <Button
            type="primary"
            htmlType="submit"
            // style={{ marginLeft: "40px" }}
          >
            {t("bookings.buttons.filter", { count: 1 })}
          </Button>
          <Button onClick={onReset}>{t("bookings.buttons.reset")}</Button>
          <Button
            type="dashed"
            className="advanced-filters-button"
            onClick={toggleAdvancedFilters}
          >
            {t("bookings.buttons.advancedFilters")}
            {openAdvancedFilters ? <UpOutlined /> : <DownOutlined />}
          </Button>
        </ContentFilters.ButtonsWrapper>
      )}
    </Form>
  );
};
